import _styled, { default as _styled2, default as _styled3, default as _styled4 } from "styled-components";
const _excluded = ["style", "className", "children", "variant", "now", "size"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import styledC, { css, keyframes } from 'styled-components';
import { useInstanceId } from '@adsk/alloy-react-helpers';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
const spin = keyframes(["100%{transform:rotate(360deg);}"]);
const spinAnimation = css(["animation:", " 0.6s linear infinite;"], spin);
const PROGRESS_RING_SIZES = {
  XSMALL: 'xsmall',
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  XLARGE: 'xlarge'
};
const PROGRESS_RING_VARIANTS = {
  DEFAULT: 'default',
  ALTERNATIVE: 'alternative'
};
const mapConstToSize = {
  [PROGRESS_RING_SIZES.XLARGE]: 216,
  [PROGRESS_RING_SIZES.LARGE]: 128,
  [PROGRESS_RING_SIZES.MEDIUM]: 64,
  [PROGRESS_RING_SIZES.SMALL]: 24,
  [PROGRESS_RING_SIZES.XSMALL]: 16
};
const StyledDiv = styledC.div.withConfig({
  displayName: "ProgressRing__StyledDiv",
  componentId: "sc-dc2pay-0"
})(["display:inline-block;color:", ";width:", "px;height:", "px;transform:rotate(", "deg);", ""], props => props.$variant === PROGRESS_RING_VARIANTS.ALTERNATIVE ? theme.tokens.colors.icon.inverse.value : theme.tokens.colors.icon.load.value, props => props.$size, props => props.$size, props => props.$indeterminate ? 0 : -90, props => props.$indeterminate ? spinAnimation : '');

/**
 * ProgressRings can be used to show the loading state in your projects.
 */
var _StyledStyledDiv = styledC(StyledDiv).withConfig({
  displayName: "ProgressRing___StyledStyledDiv",
  componentId: "sc-dc2pay-1"
})(["", ""], p => p.$_css);
const ProgressRing = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      style,
      className,
      children: _children,
      variant = PROGRESS_RING_VARIANTS.DEFAULT,
      now,
      size = PROGRESS_RING_SIZES.SMALL
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const sizeInPixels = mapConstToSize[size];
  const THICKNESS = size === PROGRESS_RING_SIZES.XSMALL ? 2 : 4;
  const circumference = 2 * Math.PI * ((sizeInPixels - THICKNESS) / 2);
  const fixedCircumference = +circumference.toFixed(3);
  const circleProps = {
    cx: sizeInPixels,
    cy: sizeInPixels,
    r: (sizeInPixels - THICKNESS) / 2,
    fill: 'none',
    strokeWidth: THICKNESS
  };
  const indeterminate = isNaN(now);
  const alternative = variant === PROGRESS_RING_VARIANTS.ALTERNATIVE;
  const instanceId = `alloy-progress-ring-${useInstanceId()}`;
  return /*#__PURE__*/React.createElement(_StyledStyledDiv, _extends({
    role: "progressbar",
    "aria-label": "progressbar",
    "aria-valuetext": indeterminate ? 'indeterminate' : undefined,
    "aria-valuenow": indeterminate ? undefined : now,
    "aria-valuemin": 0,
    "aria-valuemax": 100
  }, props, {
    className: className,
    ref: ref,
    $indeterminate: indeterminate,
    $variant: variant,
    $size: sizeInPixels,
    $_css: style
  }), /*#__PURE__*/React.createElement(_StyledSvg, {
    viewBox: `${sizeInPixels / 2} ${sizeInPixels / 2} ${sizeInPixels} ${sizeInPixels}`,
    $_css2: [{
      display: 'block'
    }]
  }, /*#__PURE__*/React.createElement("defs", {
    role: "presentation"
  }, /*#__PURE__*/React.createElement("linearGradient", {
    gradientTransform: "rotate(270)",
    id: `${instanceId}-indeterminateBlue`,
    x1: "50%",
    y1: "100%",
    x2: "50%",
    y2: "0%"
  }, /*#__PURE__*/React.createElement("stop", {
    offset: "0%",
    stopOpacity: "0",
    stopColor: theme.tokens.colors.icon.load.value
  }), /*#__PURE__*/React.createElement("stop", {
    offset: "100%",
    stopOpacity: "1",
    stopColor: theme.tokens.colors.icon.load.value
  })), /*#__PURE__*/React.createElement("linearGradient", {
    gradientTransform: "rotate(270)",
    id: `${instanceId}-indeterminateWhite`,
    x1: "50%",
    y1: "100%",
    x2: "50%",
    y2: "0%"
  }, /*#__PURE__*/React.createElement("stop", {
    offset: "0%",
    stopOpacity: "0",
    stopColor: theme.tokens.colors.icon.inverse.value
  }), /*#__PURE__*/React.createElement("stop", {
    offset: "100%",
    stopOpacity: "1",
    stopColor: theme.tokens.colors.icon.inverse.value
  }))), /*#__PURE__*/React.createElement(_StyledCircle, _extends({}, circleProps, {
    $_css3: [{
      stroke: alternative ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, .05)'
    }]
  })), /*#__PURE__*/React.createElement(_StyledCircle2, _extends({}, circleProps, {
    $_css4: [_objectSpread({
      strokeLinecap: 'round'
    }, indeterminate ? {
      stroke: alternative ? `url(#${instanceId}-indeterminateWhite)` : `url(#${instanceId}-indeterminateBlue)`,
      strokeDasharray: fixedCircumference,
      strokeDashoffset: fixedCircumference / 2
    } : {
      stroke: 'currentColor',
      strokeDasharray: fixedCircumference,
      strokeDashoffset: `${(100 - Math.min(now !== null && now !== void 0 ? now : 0, 100)) / 100 * fixedCircumference}px`,
      transition: 'stroke-dashoffset linear 0.2s'
    })]
  }))));
});
ProgressRing.displayName = 'ProgressRing';
ProgressRing.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /**  The component's children */
  children: PropTypes.any,
  /** Current ProgressRing value, if omitted would animate automatically. */
  now: PropTypes.number,
  /** The ProgressRing variant */
  variant: PropTypes.oneOf(Object.values(PROGRESS_RING_VARIANTS)),
  /**  The size of the ProgressRing, defaults to 'small' */
  size: PropTypes.oneOf(Object.values(PROGRESS_RING_SIZES))
};
export default Object.assign(ProgressRing, {
  SIZES: PROGRESS_RING_SIZES,
  VARIANTS: PROGRESS_RING_VARIANTS
});
var _StyledSvg = styledC("svg").withConfig({
  displayName: "ProgressRing___StyledSvg",
  componentId: "sc-dc2pay-2"
})(["", ""], p => p.$_css2);
var _StyledCircle = styledC("circle").withConfig({
  displayName: "ProgressRing___StyledCircle",
  componentId: "sc-dc2pay-3"
})(["", ""], p => p.$_css3);
var _StyledCircle2 = styledC("circle").withConfig({
  displayName: "ProgressRing___StyledCircle2",
  componentId: "sc-dc2pay-4"
})(["", ""], p => p.$_css4);
//# sourceMappingURL=ProgressRing.js.map